import { forwardRef, useState, useCallback } from 'react'

import styles from './CustomerInfoForm.module.css'
import { Input } from '../../components/Input'

import type { Customer } from '@checkout/core'

export interface CustomerInfoFormProps {
  initialValues?: Partial<Customer>
  onSubmit: (customer: Customer) => void
  requireName?: boolean
  requirePhone?: boolean
  isLoading?: boolean
  submitLabel?: string
  emailLabel?: string
  nameLabel?: string
  phoneLabel?: string
  className?: string
}

interface FormErrors {
  email?: string
  name?: string
  phone?: string
}

export const CustomerInfoForm = forwardRef<HTMLFormElement, CustomerInfoFormProps>(
  (
    {
      initialValues,
      onSubmit,
      requireName = false,
      requirePhone = false,
      isLoading = false,
      submitLabel = 'Continue',
      emailLabel = 'Email',
      nameLabel = 'Name',
      phoneLabel = 'Phone',
      className,
    },
    ref
  ) => {
    const [email, setEmail] = useState(initialValues?.email ?? '')
    const [name, setName] = useState(initialValues?.name ?? '')
    const [phone, setPhone] = useState(initialValues?.phone ?? '')
    const [errors, setErrors] = useState<FormErrors>({})
    const [touched, setTouched] = useState<Record<string, boolean>>({})

    const validateEmail = (value: string): string | undefined => {
      if (!value.trim()) {
        return 'Email is required'
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(value)) {
        return 'Please enter a valid email address'
      }
      return undefined
    }

    const validateName = (value: string): string | undefined => {
      if (requireName && !value.trim()) {
        return 'Name is required'
      }
      return undefined
    }

    const validatePhone = (value: string): string | undefined => {
      if (requirePhone && !value.trim()) {
        return 'Phone is required'
      }
      if (value.trim()) {
        const digitsOnly = value.replace(/\D/g, '')
        if (digitsOnly.length < 10 || digitsOnly.length > 15) {
          return 'Please enter a valid phone number'
        }
      }
      return undefined
    }

    const validateAll = useCallback((): boolean => {
      const newErrors: FormErrors = {
        email: validateEmail(email),
        name: validateName(name),
        phone: validatePhone(phone),
      }

      setErrors(newErrors)
      return !Object.values(newErrors).some(Boolean)
    }, [email, name, phone, requireName, requirePhone])

    const handleBlur = (field: keyof FormErrors) => {
      setTouched((prev) => ({ ...prev, [field]: true }))

      let error: string | undefined
      switch (field) {
        case 'email':
          error = validateEmail(email)
          break
        case 'name':
          error = validateName(name)
          break
        case 'phone':
          error = validatePhone(phone)
          break
      }

      setErrors((prev) => ({ ...prev, [field]: error }))
    }

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault()

      setTouched({ email: true, name: true, phone: true })

      if (!validateAll()) {
        return
      }

      const customer: Customer = {
        email: email.trim(),
        ...(name.trim() && { name: name.trim() }),
        ...(phone.trim() && { phone: phone.trim() }),
      }

      onSubmit(customer)
    }

    return (
      <form
        ref={ref}
        className={`${styles.form} ${className ?? ''}`}
        onSubmit={handleSubmit}
        noValidate
      >
        <div className={styles.field}>
          <Input
            type="email"
            label={emailLabel}
            value={email}
            onChange={(e) => {
              const value = e.target.value
              setEmail(value)
              // 实时校验：输入后立即校验（而非仅在 touched 后）
              // 但只有在已触及或有内容时才显示错误，避免首次聚焦就报错
              if (touched.email || value.trim()) {
                setErrors((prev) => ({ ...prev, email: validateEmail(value) }))
                if (!touched.email && value.trim()) {
                  setTouched((prev) => ({ ...prev, email: true }))
                }
              }
            }}
            onBlur={() => handleBlur('email')}
            error={touched.email ? errors.email : undefined}
            placeholder="your@email.com"
            required
            disabled={isLoading}
            autoComplete="email"
            data-ga-module="customer_info"
          />
        </div>

        <div className={styles.field}>
          <Input
            type="text"
            label={nameLabel}
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              if (touched.name) {
                setErrors((prev) => ({ ...prev, name: validateName(e.target.value) }))
              }
            }}
            onBlur={() => handleBlur('name')}
            error={touched.name ? errors.name : undefined}
            placeholder="John Doe"
            required={requireName}
            disabled={isLoading}
            autoComplete="name"
          />
        </div>

        <div className={styles.field}>
          <Input
            type="tel"
            label={phoneLabel}
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value)
              if (touched.phone) {
                setErrors((prev) => ({ ...prev, phone: validatePhone(e.target.value) }))
              }
            }}
            onBlur={() => handleBlur('phone')}
            error={touched.phone ? errors.phone : undefined}
            placeholder="+1 (555) 000-0000"
            required={requirePhone}
            disabled={isLoading}
            autoComplete="tel"
          />
        </div>

        <button type="submit" className={styles.submitButton} disabled={isLoading}>
          {isLoading ? <span className={styles.spinner} aria-hidden="true" /> : submitLabel}
        </button>
      </form>
    )
  }
)

CustomerInfoForm.displayName = 'CustomerInfoForm'
